import type {
  PriceAlertAdjustedEvent,
  PriceAlertFiredEvent,
  PriceAlertSession,
} from './price-alert-types'
import { PRICE_ALERT_SESSIONS } from './price-alert-types'

/** Prices: 2 decimals at and above $1, up to 4 below (sub-dollar stocks). */
export function formatPrice(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  const digits = Math.abs(n) >= 1 ? 2 : 4
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: digits })
}

/** The cap reason once `active` reaches `limit`, else null (also when either is unknown). */
export function priceAlertLimitMessage(
  active: number | null | undefined,
  limit: number | null | undefined,
): string | null {
  if (active == null || limit == null || active < limit) return null
  return `You have ${limit} active price alerts, the maximum. Cancel one to add another.`
}

export function conditionLabel(direction: string): string {
  return direction === 'below' ? 'Falls below' : 'Rises above'
}

const SESSION_SHORT: Record<PriceAlertSession, string> = {
  overnight: 'ON',
  premarket: 'PRE',
  regular: 'RTH',
  afterhours: 'AH',
}

export const SESSION_LABEL: Record<PriceAlertSession, string> = {
  overnight: 'Overnight',
  premarket: 'Pre-market',
  regular: 'Regular',
  afterhours: 'After-hours',
}

/** "All" when every session is on, else the short codes in session order. */
export function sessionsLabel(sessions: readonly string[]): string {
  const set = new Set(sessions)
  if (PRICE_ALERT_SESSIONS.every((s) => set.has(s))) return 'All'
  return PRICE_ALERT_SESSIONS.filter((s) => set.has(s))
    .map((s) => SESSION_SHORT[s])
    .join(' · ')
}

/** Select label: "All sessions" when every session is on, "No sessions" when none, else the short codes. */
export function sessionsSelectLabel(sessions: readonly string[]): string {
  if (sessions.length === 0) return 'No sessions'
  const label = sessionsLabel(sessions)
  return label === 'All' ? 'All sessions' : label
}

/** Local "Oct 8, 10:42" (time only for today). */
export function formatTimestamp(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  if (d.toDateString() === now.toDateString()) return time
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
}

export function firedMessage(e: PriceAlertFiredEvent): string {
  if (e.message) return e.message
  const verb = e.direction === 'below' ? 'fell below' : 'rose above'
  const at = e.trigger_price != null ? ` at ${formatPrice(e.trigger_price)}` : ''
  return `${e.symbol} ${verb} ${formatPrice(e.level)}${at}${e.gapped ? ' (gapped through)' : ''}`
}

export function adjustedMessage(e: PriceAlertAdjustedEvent): string {
  if (e.message) return e.message
  const who = e.symbol ? `${e.symbol} alert` : 'Price alert'
  if (e.old_level != null && e.level != null) {
    return `${who} adjusted for a split: ${formatPrice(e.old_level)} → ${formatPrice(e.level)}`
  }
  return `${who} adjusted for a split`
}
